import config from 'config';
import { ButtonIcon, Card, Text, ButtonSolid, Container } from 'ui/components/primitives';
import { mixins, styled, theme, utils } from 'ui/theme';

const headerSize = {
  mobile: theme.sizes[18],
  desktop: `calc(${theme.sizes[22]} + ${theme.space[17]})`,
};

export const GiftStyles = {
  Header: {
    Section: styled.div`
      background-color: ${theme.colors.build.lightDark2};
      width: 100%;
      display: flex;
      justify-content: center;
      ${utils.media.max('m')`
        margin-bottom: 64px;
      `}
      }
    `,
    Component: styled.header`
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      color: ${theme.colors.build.darkLight};
      padding: 0 ${theme.space[4]} 0;
      height: 64px;
      width: 100%;
      max-width: ${utils.containerMaxWidth}px;
      z-index: ${theme.zIndices.header};
      flex-direction: row;
    `,
    Container: styled.nav`
      margin: 0 auto;
      padding: 0;
      position: relative;
      width: 100%;
      display: flex;
      justify-content: center;
      gap: ${theme.space[4]};
      ${utils.media.max('m')`
        justify-content: flex-start;
      `}
    `,
    Space: styled.div`
      padding-top: ${headerSize.mobile};
      ${utils.media.min('l')`
        padding-top: ${headerSize.desktop};
      `}
    `,
    Button: styled(ButtonIcon)`
      svg {
        margin-top: 1px;
      }
    `,
  },
  WrapperCarousel: styled.div`
    background-color: white;
    margin: 0 calc(-1 * ${theme.space[4]});
    overflow: hidden;
    position: relative;

    ${utils.media.min('l')`
      padding: 0;
      margin: 0;
      overflow: initial;
    `}
  `,
  Sku: styled(Card)`
    border: 1px solid ${theme.colors.build.lightDark1};
    border-radius: 4px;
    color: ${theme.colors.build.darkLight};
    display: flex;
    flex-flow: column;
    gap: ${theme.space[2]};
    height: 100%;
    justify-content: flex-end;
    &[data-visible='false'] {
      display: none;
    }
  `,
  Skus: styled('div')`
    background: ${theme.colors.build.lightDark2};
    grid-column: 1 / -1;
    margin-bottom: 1px;
    padding: ${theme.space[6]} ${theme.space[4]};

    ${utils.media.min('l')`
      padding: ${theme.space[6]} ${theme.space[10]};
    `}

    &:nth-first-of-type(1) {
      border-radius: ${theme.radii.small} ${theme.radii.small} 0 0;
    }

    &:nth-last-of-type(1) {
      border-radius: 0 0 ${theme.radii.small} ${theme.radii.small};
    }

    ${utils.media.min('l')`
      grid-column: 4 / span 9;
    `}
  `,
  Tag: styled('div')`
    ${mixins.baseFont};
    align-items: end;
    background: ${theme.colors.build.lightDark1};
    border-radius: ${theme.radii.small};
    box-shadow: 0 0 0 1px ${theme.colors.status.alert.standard};
    color: ${theme.colors.build.darkLight};
    display: inline-flex;
    gap: ${theme.space[1]};
    height: ${theme.space[6]};
    padding: ${theme.space[1]} ${theme.space[2]};

    &[data-completed='true'] {
      box-shadow: 0 0 0 1px ${theme.colors.status.success.standard};
    }

    span {
      vertical-align: ${config.app.short === 'eud' ? 'bottom' : 'sub'};
    }
  `,
  SkuNumber: styled(Text)`
    span {
      background: ${theme.colors.build.lightDark1};
    }
  `,
  TextName: styled(Text)`
    ${utils.lineClamp(2)}
    &[data-extra-margin] {
      margin-top: 6px;
    }
  `,
  Options: styled('div')`
    border-radius: 4px;
    grid-column: 1 / -1;
    overflow: hidden;
    row-gap: ${theme.space[1]};

    ${utils.media.min('l')`
      grid-column: 4 / span 9;
    `}
  `,
  Wrapper: styled('div')`
    ${utils.grid.size[12]};
    column-gap: ${theme.space[4]};
    row-gap: ${theme.space[1]};
    padding-bottom: ${theme.space[12]};
  `,
  CardHeader: {
    Container: styled('div')`
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      &[data-next-order-allowed='false'] {
        margin-bottom: 16px;
      }
    `,
    Title: styled('span')`
      ${mixins.baseFont};
      margin-right: 16px;
      font-size: 24px;
      color: ${theme.colors.build.darkLight};
      ${utils.media.max('m')`
        font-size: 20px;
        margin-right: 26px;
      `}
    `,
    NextOrderAllowed: styled('div')`
      border-radius: 4px;
      min-height: 28px;
      display: flex;
      margin-bottom: 16px;
      svg {
        margin-right: 4px;
      }
    `,
    NextOrderAllowedText: styled(Text)`
      margin-top: 3px;
    `,
  },
  ActionButton: styled(ButtonSolid)`
    align-items: center;
    display: flex;
    margin-top: ${theme.space[2]};
    height: 48px;
  `,
  QuantityWrapper: styled('div')`
    margin-top: ${theme.space[2]};
  `,
  SubmitButton: styled(ButtonSolid)`
    display: flex;
    margin: 24px 0 80px;
    width: 255px;
    align-self: end;
    padding: 16px 20px;
    svg {
      height: 16px;
      width: 16px;
      margin: 1px 0 0 8px;
    }
    ${utils.media.max('m')`
      margin: 0;
      position: fixed;
      width: 100%;
      border-radius: 0;
      bottom: 0;
      left: 0;
      z-index: 100;
      &[disabled] {
        background-color: #d2d2d2;
      }
    `}
    }
  `,
  GiftsForm: styled('form')``,
  GiftsContainer: styled(Container)`
    display: flex;
    flex-direction: column;
    ${utils.media.max('m')`
      padding: 0 0 48px;
    `}
  `,
  RemainingContentIndicator: styled('div')`
    width: 50px;
    height: 100%;
    position: absolute;
    right: 0;
    z-index: 1;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
    pointer-events: none;
  `,
};
